import React, { Fragment, ReactNode } from 'react';

import { Price } from '@zapier/currency';
import { Button, Link } from '@zapier/design-system';

import { Interval } from '@zapier/design-system-revenue/src/types';

import { UpgradeButtonToPricingPage } from '@zapier/design-system-revenue/src/components/offers/UpgradeModal/UpgradeReason/UpgradeButtonToPricingPage';

import { Props as TaskLimit80PercentUpgradeNudgeProps } from '../TaskLimit80PercentUpgradeNudge';

const monthlyAmount = (amount: number, interval: Interval) =>
  interval === 'year' ? amount / 12 : amount;

const ContinueButton = ({ onClick }: { onClick?: () => void }) => {
  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <Button color="greyAlt" onClick={handleClick}>
      Continue
    </Button>
  );
};

type ViewType = 'DEFAULT';

type View = {
  bodyText: ReactNode;
  buttons: ReactNode[];
  type: ViewType;
};

/**
 * Note: Upgrade buttons in these views take the user to the upgrade
 * page rather than doing the upgrade inline because these nudges could
 * be used in `web-ssr`, and the payment app doesn't work in `web-ssr`.
 * This also gives the user the chance to change their billing interval,
 * which is common for annual users who need to upgrade to the next
 * task limit just to get them through the rest of the month.
 */
const views: {
  [key in ViewType]: (...args: any[]) => View;
} = {
  ['DEFAULT']: ({ currentPlan, nextPlan, tasksRemaining }) => ({
    bodyText: (
      <Fragment>
        You have {tasksRemaining.toLocaleString()} task
        {tasksRemaining !== 1 && <span>s</span>} left until your monthly usage
        resets. Upgrade your plan to get{' '}
        {Number(nextPlan.tasks).toLocaleString()}{' '}
        <Link
          href="/help/manage/tasks/learn-about-tasks-in-zapier"
          target="_blank"
        >
          tasks
        </Link>{' '}
        per month for{' '}
        <Price>{monthlyAmount(nextPlan.amount, nextPlan.interval)}</Price>
        /month.
      </Fragment>
    ),
    buttons: [
      <UpgradeButtonToPricingPage
        currentPlanInterval={currentPlan.interval}
        currentPlanName={currentPlan.name}
        nextPlanName={nextPlan.name}
      />,

      <ContinueButton />,
    ],
    type: 'DEFAULT',
  }),
};

export const getActiveView = ({
  activeTrialType,
  currentPlan,
  lowestTeamOrCompanyPlan,
  nextPlan,
  tasksRemaining,
}: Omit<TaskLimit80PercentUpgradeNudgeProps, 'onClose'>) =>
  activeTrialType === 'Team' || activeTrialType === 'Company'
    ? views.DEFAULT({
        currentPlan,
        nextPlan: lowestTeamOrCompanyPlan,
        tasksRemaining,
      })
    : views.DEFAULT({
        currentPlan,
        nextPlan,
        tasksRemaining,
      });
